import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthenticated } from "../auth/SessionProvider";
import { post } from "./client";
import type {
  CandidateDetail,
  CandidateListRequest,
  CandidateListResponse,
  CandidateSummary,
  Group,
  OverviewResponse,
  RosterImportResponse,
  SetEnabledBySiteResponse,
  SetEnabledResponse
} from "./types";

export function useCandidates(request: CandidateListRequest) {
  const { token } = useAuthenticated();
  return useQuery({
    queryKey: ["candidates", token, request],
    queryFn: () => post<CandidateListResponse>("/candidates/list", request, token),
    placeholderData: (previous) => previous
  });
}

export function useCandidate(username: string | undefined) {
  const { token } = useAuthenticated();
  return useQuery({
    queryKey: ["candidate", token, username],
    queryFn: () => post<CandidateDetail>("/candidates/get", { username }, token),
    enabled: username !== undefined
  });
}

export function useOverview() {
  const { token } = useAuthenticated();
  return useQuery({
    queryKey: ["overview", token],
    queryFn: () => post<OverviewResponse>("/overview/sites", {}, token),
    refetchInterval: 30_000
  });
}

/** Every candidate of one site, across pages, for the site-level batch dialog. */
export async function fetchSiteCandidates(token: string, siteId: number, roomId?: number): Promise<CandidateSummary[]> {
  const items: CandidateSummary[] = [];
  for (let page = 1; ; page += 1) {
    const result = await post<CandidateListResponse>("/candidates/list", { siteId, roomId, page, pageSize: 100 }, token);
    items.push(...result.items);
    if (items.length >= result.total || result.items.length === 0) break;
  }
  return items;
}

/** Every candidate of one group, across pages, for the group-level batch dialog. */
export async function fetchGroupCandidates(token: string, group: Group): Promise<CandidateSummary[]> {
  const items: CandidateSummary[] = [];
  for (let page = 1; ; page += 1) {
    const result = await post<CandidateListResponse>("/candidates/list", { group, page, pageSize: 100 }, token);
    items.push(...result.items);
    if (items.length >= result.total || result.items.length === 0) break;
  }
  return items;
}

function useInvalidateCandidates() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["candidates"] });
    void client.invalidateQueries({ queryKey: ["candidate"] });
    void client.invalidateQueries({ queryKey: ["overview"] });
  };
}

export function useSetEnabled() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: (input: { usernames: string[]; enabled: boolean }) =>
      post<SetEnabledResponse>("/candidates/set-enabled", input, token),
    onSuccess: invalidate
  });
}

export function useSetEnabledBySite() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: (input: { siteId: number; roomId?: number; enabled: boolean; exclude: string[] }) =>
      post<SetEnabledBySiteResponse>("/candidates/set-enabled-by-site", input, token),
    onSuccess: invalidate
  });
}

export function useSetEnabledByGroup() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: (input: { group: Group; enabled: boolean; exclude: string[] }) =>
      post<SetEnabledBySiteResponse>("/candidates/set-enabled-by-group", input, token),
    onSuccess: invalidate
  });
}

export function useImportRoster() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidateCandidates();
  return useMutation({
    mutationFn: (input: { filename: string; contentBase64: string; group: Group; dryRun: boolean }) =>
      post<RosterImportResponse>("/roster/import", input, token),
    onSuccess: (result) => {
      if (result.ok && !result.dryRun) invalidate();
    }
  });
}
